import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { getPalette, oklchToCss, slotColor } from '@finbob/shared';
import { usePreferencesStore } from '@/stores/preferences.store';
import { celebrationParticleCount, useEffectiveReducedMotion } from '@/lib/motion';
import { prefersReducedAmbientMotion } from '@/lib/deviceCapability';
export type CelebrationStyle = 'burst' | 'fountain' | 'sparkles';
const styles: CelebrationStyle[] = ['burst', 'fountain', 'sparkles'];
/** Full viewport, non-blocking celebration. A static border glow replaces movement when reduced. */
export function Confetti({ trigger, variant }: { trigger: number; variant?: CelebrationStyle }) {
  const paletteId = usePreferencesStore((s) => s.palette);
  const reduced = useEffectiveReducedMotion();
  const [completedTrigger, setCompletedTrigger] = useState(trigger);
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    if (trigger === completedTrigger) return;
    const timer = setTimeout(() => setCompletedTrigger(trigger), 2300);
    return () => clearTimeout(timer);
  }, [trigger, completedTrigger]);
  useEffect(() => {
    const hide = () => {
      setVisible(document.visibilityState !== 'hidden');
      // Never replay a completion that happened while the user was in another tab.
      setCompletedTrigger(trigger);
    };
    document.addEventListener('visibilitychange', hide);
    return () => document.removeEventListener('visibilitychange', hide);
  }, [trigger]);
  if (trigger === completedTrigger || !visible) return null;
  const style = variant ?? styles[trigger % styles.length]!;
  if (reduced)
    return (
      <div
        aria-hidden="true"
        data-celebration="still"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100,
          pointerEvents: 'none',
          boxShadow: 'inset 0 0 24px color-mix(in srgb, var(--accent) 20%, transparent)',
        }}
      />
    );
  const palette = getPalette(paletteId);
  const colors = [0, 1, 2, 3, 4, 5].map((i) => oklchToCss(slotColor(palette, i)));
  const count = celebrationParticleCount(style === 'sparkles', prefersReducedAmbientMotion());
  return (
    <div
      key={trigger}
      aria-hidden="true"
      data-celebration={style}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: count }, (_, i) => {
        const phase = ((i * 37 + trigger * 13) % 101) / 100;
        const angle = (i / count) * Math.PI * 2;
        const fountain = style === 'fountain';
        const sparkle = style === 'sparkles';
        const left = fountain ? (i % 2 ? '94%' : '6%') : sparkle ? `${8 + phase * 84}%` : '50%';
        const top = fountain ? '82%' : sparkle ? `${12 + ((i * 23) % 72)}%` : '48%';
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.4, rotate: 0 }}
            animate={{
              x: sparkle
                ? [0, 0, 0]
                : fountain
                  ? [
                      0,
                      (i % 2 ? -1 : 1) * (100 + phase * 260),
                      (i % 2 ? -1 : 1) * (180 + phase * 400),
                    ]
                  : [
                      0,
                      Math.cos(angle) * (100 + phase * 200),
                      Math.cos(angle) * (180 + phase * 330),
                    ],
              y: sparkle
                ? [0, -15, -35]
                : fountain
                  ? [0, -220 - phase * 250, 80 + phase * 130]
                  : [
                      0,
                      Math.sin(angle) * (140 + phase * 220),
                      Math.sin(angle) * (140 + phase * 220) + 200,
                    ],
              opacity: [0, 1, 0],
              scale: sparkle ? [0.3, 1.5, 0] : [0.5, 1, 0.7],
              rotate: sparkle ? [0, 45, 90] : [0, 160, 360 + phase * 360],
            }}
            transition={{ duration: sparkle ? 1.3 : 1.85, delay: (i % 8) * 0.045, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              left,
              top,
              width: sparkle ? 16 : 7 + phase * 6,
              height: sparkle ? 16 : 5 + phase * 9,
              borderRadius: sparkle ? 0 : i % 3 === 0 ? '50%' : 2,
              clipPath: sparkle
                ? 'polygon(50% 0,62% 38%,100% 50%,62% 62%,50% 100%,38% 62%,0 50%,38% 38%)'
                : undefined,
              background: colors[i % colors.length],
              boxShadow: sparkle ? `0 0 14px ${colors[i % colors.length]}` : undefined,
            }}
          />
        );
      })}
    </div>
  );
}
